import api from '@disco/api/server'
import { pushUINotification } from '@disco/utils/notifications'
import type {
  ClientLibraryContentGroupInput,
  ClientLibraryContentItem,
  ClientLibraryId,
} from 'types/client-library'

import { api as rtkApi } from './index'

export const clientLibrariesApi = rtkApi
  .enhanceEndpoints({ addTagTypes: ['ClientLibraryContent'] })
  .injectEndpoints({
    endpoints: build => ({
      getClientLibraryContent: build.query<
        ClientLibraryContentItem[],
        ClientLibraryId
      >({
        queryFn: async libraryId => {
          try {
            const byId = new Map<number, ClientLibraryContentItem>()
            let response = await api.getClientLibraryContent(libraryId)
            response.results.forEach(item => byId.set(item.id, item))
            while (response.next) {
              response = await api.getUrl(
                response.next,
                null,
                'api.getClientLibraryContentNext',
              )
              response.results.forEach(item => byId.set(item.id, item))
            }
            return { data: Array.from(byId.values()) }
          } catch (error) {
            return { error }
          }
        },
        providesTags: (_result, _error, libraryId) => [
          { type: 'ClientLibraryContent', id: libraryId },
        ],
      }),
      alterClientLibraryGroups: build.mutation<
        unknown,
        {
          libraryId: ClientLibraryId
          to_add: ClientLibraryContentGroupInput[]
          to_remove: number[]
        }
      >({
        queryFn: async ({ libraryId, to_add, to_remove }) => {
          try {
            const data = await api.alterClientLibraryGroups(
              libraryId,
              to_add,
              to_remove,
            )
            return { data }
          } catch (error) {
            return { error }
          }
        },
        invalidatesTags: (_result, error, { libraryId }) =>
          error ? [] : [{ type: 'ClientLibraryContent', id: libraryId }],
        async onQueryStarted(_args, { queryFulfilled }) {
          try {
            await queryFulfilled
            pushUINotification({
              message: 'Content updated!',
              level: 'success',
            })
          } catch {
            pushUINotification({
              message: 'Failed to update content. Please try again.',
              level: 'error',
            })
          }
        },
      }),
    }),
  })

export const {
  useGetClientLibraryContentQuery,
  useAlterClientLibraryGroupsMutation,
} = clientLibrariesApi
